/**
 * Feststehende Beschriftungen des Dokuments in mehreren Sprachen — bewusst
 * ein von Hand gepflegtes Wörterbuch, kein Claude-Aufruf beim Export.
 * Ein PDF wird oft direkt nach der Unterschrift erzeugt, unter Umständen
 * ohne Netz (Keller, Tiefgarage) — Übersetzung darf dafür keine
 * Voraussetzung sein. Übersetzt werden nur die Beschriftungen (Räume,
 * Zustand, Rollen, Tabellenköpfe); die diktierten Mangel-Beschreibungen
 * bleiben in der Sprache, in der sie erfasst wurden — sie automatisch zu
 * übersetzen würde das Risiko einer falschen Übersetzung ausgerechnet bei
 * der Stelle eingehen, auf die es im Streitfall ankommt.
 */
import type { Beteiligter, Zaehler, Zustand } from './domain/types';

export type SpracheCode = 'de' | 'en' | 'tr' | 'pl' | 'ru' | 'ar';

export interface DokumentTexte {
	label: string;
	rtl: boolean;
	titel: (art: 'einzug' | 'auszug') => string;
	erstelltAm: string;
	maengelAnzahl: (n: number) => string;
	raeume: string;
	keineRaeumeErfasst: string;
	keineAuffaelligkeiten: string;
	nichtErfasst: string;
	zaehlerstaende: string;
	art: string;
	nummer: string;
	stand: string;
	schluessel: string;
	anzahl: string;
	unterschriften: string;
	nochNichtUnterschrieben: string;
	erstelltMit: string;
	foto: string;
	zustand: Record<Zustand, string>;
	rolle: Record<Beteiligter['rolle'], string>;
	zaehlerArt: Record<Zaehler['art'], string>;
}

export const SPRACHEN: { code: SpracheCode; label: string }[] = [
	{ code: 'de', label: 'Deutsch' },
	{ code: 'en', label: 'English' },
	{ code: 'tr', label: 'Türkçe' },
	{ code: 'pl', label: 'Polski' },
	{ code: 'ru', label: 'Русский' },
	{ code: 'ar', label: 'العربية' }
];

const DE: DokumentTexte = {
	label: 'Deutsch',
	rtl: false,
	titel: (art) => `Übergabeprotokoll — ${art === 'einzug' ? 'Einzug' : 'Auszug'}`,
	erstelltAm: 'Erstellt am',
	maengelAnzahl: (n) => (n === 1 ? '1 Mangel' : `${n} Mängel`),
	raeume: 'Räume',
	keineRaeumeErfasst: 'Keine Räume erfasst.',
	keineAuffaelligkeiten: 'Keine Auffälligkeiten.',
	nichtErfasst: 'Nicht erfasst',
	zaehlerstaende: 'Zählerstände',
	art: 'Art',
	nummer: 'Nummer',
	stand: 'Stand',
	schluessel: 'Schlüssel',
	anzahl: 'Anzahl',
	unterschriften: 'Unterschriften',
	nochNichtUnterschrieben: 'Noch nicht unterschrieben',
	erstelltMit: 'Erstellt mit',
	foto: 'Foto',
	zustand: { in_ordnung: 'In Ordnung', gebrauchsspuren: 'Gebrauchsspuren', mangel: 'Mangel' },
	rolle: { uebergeber: 'Übergeber', uebernehmer: 'Übernehmer', zeuge: 'Zeuge' },
	zaehlerArt: {
		strom: 'Strom',
		gas: 'Gas',
		wasser_kalt: 'Wasser kalt',
		wasser_warm: 'Wasser warm',
		heizung: 'Heizung',
		sonstige: 'Sonstige'
	}
};

const EN: DokumentTexte = {
	label: 'English',
	rtl: false,
	titel: (art) => `Handover Protocol — ${art === 'einzug' ? 'Move-in' : 'Move-out'}`,
	erstelltAm: 'Created on',
	maengelAnzahl: (n) => `${n} issue${n === 1 ? '' : 's'}`,
	raeume: 'Rooms',
	keineRaeumeErfasst: 'No rooms recorded.',
	keineAuffaelligkeiten: 'No issues noted.',
	nichtErfasst: 'Not recorded',
	zaehlerstaende: 'Meter readings',
	art: 'Type',
	nummer: 'Number',
	stand: 'Reading',
	schluessel: 'Keys',
	anzahl: 'Quantity',
	unterschriften: 'Signatures',
	nochNichtUnterschrieben: 'Not yet signed',
	erstelltMit: 'Created with',
	foto: 'Photo',
	zustand: { in_ordnung: 'Fine', gebrauchsspuren: 'Signs of wear', mangel: 'Defect' },
	rolle: { uebergeber: 'Handing over', uebernehmer: 'Receiving', zeuge: 'Witness' },
	zaehlerArt: {
		strom: 'Electricity',
		gas: 'Gas',
		wasser_kalt: 'Cold water',
		wasser_warm: 'Hot water',
		heizung: 'Heating',
		sonstige: 'Other'
	}
};

const TR: DokumentTexte = {
	label: 'Türkçe',
	rtl: false,
	titel: (art) => `Teslim Tutanağı — ${art === 'einzug' ? 'Taşınma (Giriş)' : 'Taşınma (Çıkış)'}`,
	erstelltAm: 'Oluşturulma tarihi',
	maengelAnzahl: (n) => `${n} sorun`,
	raeume: 'Odalar',
	keineRaeumeErfasst: 'Kayıtlı oda yok.',
	keineAuffaelligkeiten: 'Herhangi bir sorun yok.',
	nichtErfasst: 'Kaydedilmedi',
	zaehlerstaende: 'Sayaç değerleri',
	art: 'Tür',
	nummer: 'Numara',
	stand: 'Değer',
	schluessel: 'Anahtarlar',
	anzahl: 'Adet',
	unterschriften: 'İmzalar',
	nochNichtUnterschrieben: 'Henüz imzalanmadı',
	erstelltMit: 'Şununla oluşturuldu:',
	foto: 'Fotoğraf',
	zustand: { in_ordnung: 'İyi durumda', gebrauchsspuren: 'Kullanım izleri', mangel: 'Hasar' },
	rolle: { uebergeber: 'Teslim eden', uebernehmer: 'Teslim alan', zeuge: 'Tanık' },
	zaehlerArt: {
		strom: 'Elektrik',
		gas: 'Gaz',
		wasser_kalt: 'Soğuk su',
		wasser_warm: 'Sıcak su',
		heizung: 'Isıtma',
		sonstige: 'Diğer'
	}
};

const PL: DokumentTexte = {
	label: 'Polski',
	rtl: false,
	titel: (art) => `Protokół przekazania — ${art === 'einzug' ? 'Wprowadzenie' : 'Wyprowadzenie'}`,
	erstelltAm: 'Utworzono dnia',
	maengelAnzahl: (n) => `${n} usterek`,
	raeume: 'Pomieszczenia',
	keineRaeumeErfasst: 'Brak zarejestrowanych pomieszczeń.',
	keineAuffaelligkeiten: 'Brak uwag.',
	nichtErfasst: 'Nie zarejestrowano',
	zaehlerstaende: 'Stany liczników',
	art: 'Rodzaj',
	nummer: 'Numer',
	stand: 'Stan',
	schluessel: 'Klucze',
	anzahl: 'Ilość',
	unterschriften: 'Podpisy',
	nochNichtUnterschrieben: 'Jeszcze nie podpisano',
	erstelltMit: 'Utworzono za pomocą',
	foto: 'Zdjęcie',
	zustand: { in_ordnung: 'W porządku', gebrauchsspuren: 'Ślady użytkowania', mangel: 'Usterka' },
	rolle: { uebergeber: 'Przekazujący', uebernehmer: 'Przejmujący', zeuge: 'Świadek' },
	zaehlerArt: {
		strom: 'Prąd',
		gas: 'Gaz',
		wasser_kalt: 'Zimna woda',
		wasser_warm: 'Ciepła woda',
		heizung: 'Ogrzewanie',
		sonstige: 'Inne'
	}
};

const RU: DokumentTexte = {
	label: 'Русский',
	rtl: false,
	titel: (art) => `Акт приёма-передачи — ${art === 'einzug' ? 'Въезд' : 'Выезд'}`,
	erstelltAm: 'Дата создания',
	maengelAnzahl: (n) => `${n} замечаний`,
	raeume: 'Помещения',
	keineRaeumeErfasst: 'Помещения не зафиксированы.',
	keineAuffaelligkeiten: 'Замечаний нет.',
	nichtErfasst: 'Не зафиксировано',
	zaehlerstaende: 'Показания счётчиков',
	art: 'Тип',
	nummer: 'Номер',
	stand: 'Показание',
	schluessel: 'Ключи',
	anzahl: 'Количество',
	unterschriften: 'Подписи',
	nochNichtUnterschrieben: 'Ещё не подписано',
	erstelltMit: 'Создано с помощью',
	foto: 'Фото',
	zustand: { in_ordnung: 'В порядке', gebrauchsspuren: 'Следы использования', mangel: 'Дефект' },
	rolle: { uebergeber: 'Передающий', uebernehmer: 'Принимающий', zeuge: 'Свидетель' },
	zaehlerArt: {
		strom: 'Электричество',
		gas: 'Газ',
		wasser_kalt: 'Холодная вода',
		wasser_warm: 'Горячая вода',
		heizung: 'Отопление',
		sonstige: 'Прочее'
	}
};

const AR: DokumentTexte = {
	label: 'العربية',
	rtl: true,
	titel: (art) => `محضر تسليم — ${art === 'einzug' ? 'الانتقال إلى السكن' : 'إخلاء السكن'}`,
	erstelltAm: 'تاريخ الإنشاء',
	maengelAnzahl: (n) => `${n} ملاحظة`,
	raeume: 'الغرف',
	keineRaeumeErfasst: 'لم يتم تسجيل أي غرف.',
	keineAuffaelligkeiten: 'لا توجد ملاحظات.',
	nichtErfasst: 'غير مسجّل',
	zaehlerstaende: 'قراءات العدادات',
	art: 'النوع',
	nummer: 'الرقم',
	stand: 'القراءة',
	schluessel: 'المفاتيح',
	anzahl: 'العدد',
	unterschriften: 'التوقيعات',
	nochNichtUnterschrieben: 'لم يتم التوقيع بعد',
	erstelltMit: 'أُنشئ باستخدام',
	foto: 'صورة',
	zustand: { in_ordnung: 'بحالة جيدة', gebrauchsspuren: 'آثار استخدام', mangel: 'عيب' },
	rolle: { uebergeber: 'المُسلِّم', uebernehmer: 'المُستلِم', zeuge: 'الشاهد' },
	zaehlerArt: {
		strom: 'الكهرباء',
		gas: 'الغاز',
		wasser_kalt: 'الماء البارد',
		wasser_warm: 'الماء الساخن',
		heizung: 'التدفئة',
		sonstige: 'أخرى'
	}
};

export const UEBERSETZUNGEN: Record<SpracheCode, DokumentTexte> = { de: DE, en: EN, tr: TR, pl: PL, ru: RU, ar: AR };
